/**
 * Report Issue Dialog
 * UI for reporting issues with clips
 */

import appState from '../core/app-state.js';
import logger from '../core/logger.js';
import { logEvent } from '../core/event-logger.js';
import { EVENT_TYPES } from '../core/event-types.js';
import * as TranscriptModule from '../modules/player/transcript.js';
import type { Clip } from '../types/clip.js';

/**
 * Render a "Report Issue" button
 */
export function renderReportIssueButton(clip: Clip, summaryChip: HTMLElement): void {
  const reportContainer = document.createElement('div');
  reportContainer.style.cssText = 'margin-top: 1rem; padding-top: 1rem; border-top: 1px solid #e5e7eb;';
  
  const reportButton = document.createElement('button');
  reportButton.textContent = 'Report Issue';
  reportButton.className = 'btn--link';
  reportButton.style.cssText = 'font-size: 0.875rem; padding: 0.5rem 1rem; color: #6b7280; background: transparent; border: 1px solid #d1d5db; border-radius: 0.375rem; cursor: pointer;';
  reportButton.setAttribute('aria-label', 'Report an issue with this clip');
  
  reportButton.addEventListener('click', () => {
    showReportIssueDialog(clip, reportContainer);
  });
  
  reportContainer.appendChild(reportButton);
  summaryChip.appendChild(reportContainer);
}

/**
 * Show report issue dialog
 */
function showReportIssueDialog(clip: Clip, container: HTMLElement): void {
  container.innerHTML = '';
  
  const dialogTitle = document.createElement('div');
  dialogTitle.textContent = 'Report Issue';
  dialogTitle.style.cssText = 'font-weight: 600; margin-bottom: 0.5rem; color: #374151;';
  container.appendChild(dialogTitle);
  
  const textarea = document.createElement('textarea');
  textarea.placeholder = 'Describe the issue with this clip...';
  textarea.style.cssText = 'width: 100%; min-height: 80px; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; resize: vertical; margin-bottom: 0.5rem;';
  textarea.setAttribute('aria-label', 'Issue description');
  container.appendChild(textarea);
  
  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display: flex; gap: 0.5rem;';
  
  const copyButton = document.createElement('button');
  copyButton.textContent = 'Copy Report';
  copyButton.style.cssText = 'padding: 0.5rem 1rem; background-color: #3b82f6; color: white; font-weight: 600; border-radius: 0.375rem; border: none; cursor: pointer; font-size: 0.875rem;';
  copyButton.setAttribute('aria-label', 'Copy report to clipboard');
  
  copyButton.addEventListener('click', async () => {
    const reportText = textarea.value.trim();
    const currentTime = document.querySelector('audio')?.currentTime || 0;
    
    const questionSummary: any = {};
    if (appState.clipAnswers.has(clip.id)) {
      const answers = appState.clipAnswers.get(clip.id);
      if (answers) {
        questionSummary.totalQuestions = answers.length;
        questionSummary.correctAnswers = answers.filter((a: boolean) => a === true).length;
      }
    }
    
    const reportData = {
      clipId: clip.id,
      difficulty: clip.difficulty,
      seriesInfo: clip.storyId ? { storyId: clip.storyId, storyPart: clip.storyPart } : null,
      completed: true,
      currentTime: Math.round(currentTime * 10) / 10,
      questionInteractionSummary: questionSummary,
      reportText: reportText,
      timestamp: new Date().toISOString()
    };
    
    const reportJson = JSON.stringify(reportData, null, 2);
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(reportJson);
      } else {
        // Fallback for older browsers that don't support Clipboard API
        // execCommand is deprecated but still needed for legacy browser support
        const tempTextarea = document.createElement('textarea');
        tempTextarea.value = reportJson;
        tempTextarea.style.position = 'fixed';
        tempTextarea.style.opacity = '0';
        document.body.appendChild(tempTextarea);
        tempTextarea.select();
        document.execCommand('copy');
        document.body.removeChild(tempTextarea);
      }
      
      copyButton.textContent = 'Report Copied ✓';
      copyButton.style.backgroundColor = '#10b981';
      TranscriptModule.announceToScreenReader('Report copied to clipboard');
      
      const eventType = EVENT_TYPES['REPORT_ISSUE_COPIED'];
      if (eventType) {
        logEvent(eventType, {
          clipId: clip.id
        });
      }
      
      setTimeout(() => {
        copyButton.textContent = 'Copy Report';
        copyButton.style.backgroundColor = '#3b82f6';
      }, 2000);
    } catch (err) {
      logger.warn('Failed to copy report', err);
      TranscriptModule.announceToScreenReader('Failed to copy report');
    }
  });
  
  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.style.cssText = 'padding: 0.5rem 1rem; background: transparent; color: #6b7280; border: 1px solid #d1d5db; border-radius: 0.375rem; cursor: pointer; font-size: 0.875rem;';
  cancelButton.setAttribute('aria-label', 'Cancel report');
  
  cancelButton.addEventListener('click', () => {
    const parent = container.parentElement;
    if (parent) {
      renderReportIssueButton(clip, parent);
    }
  });
  
  buttonRow.appendChild(copyButton);
  buttonRow.appendChild(cancelButton);
  container.appendChild(buttonRow);
  
  textarea.focus();
}
